<template>
  <div class="form4-container">
    <div class="section-header">
      <h2 class="section-title">景点背景与通关知识</h2>
      <span class="section-tip"
        >编排本关卡对应的景点历史或文化背景，右侧实时同步 App 端阅读视口</span
      >
    </div>

    <div class="editor-layout">
      <!-- 左侧：表单配置区 -->
      <div class="form-area">
        <el-form
          :model="form"
          label-width="auto"
          label-position="top"
          ref="ruleForm"
          status-icon
          class="custom-form"
        >
          <el-form-item label="景点名称 / 知识标题" prop="attractionsTitle">
            <el-input
              v-model="form.attractionsTitle"
              placeholder="例如：滕王阁的建筑渊源"
              maxlength="40"
              show-word-limit
              clearable
            />
          </el-form-item>

          <el-form-item label="景点风貌图片" prop="attractionsImage">
            <div class="cover-uploader-wrapper">
              <el-upload
                class="attraction-uploader"
                :action="UpdateApi"
                :show-file-list="false"
                :on-success="handleAvatarSuccess"
                :before-upload="beforeAvatarUpload"
                :disabled="allowUpload"
              >
                <!-- 已上传：展示封面并提供悬停删除蒙层 -->
                <div v-if="form.attractionsImage" class="uploaded-box">
                  <el-image
                    :src="form.attractionsImage"
                    class="cover-preview"
                    fit="cover"
                  >
                    <template #error>
                      <div class="image-error-slot">
                        <el-icon><PictureFilled /></el-icon>
                        <span>加载失败</span>
                      </div>
                    </template>
                  </el-image>
                  <div class="cover-mask">
                    <span
                      class="action-btn"
                      @click.stop="clearImage"
                      title="删除并重新上传"
                    >
                      <el-icon><Delete /></el-icon>
                    </span>
                  </div>
                </div>

                <!-- 未上传：占位引导 -->
                <div v-else class="upload-trigger-placeholder">
                  <el-icon class="uploader-icon"><Plus /></el-icon>
                  <span class="trigger-text">点击上传景点风貌图</span>
                  <span class="trigger-sub"
                    >支持 4:3 | 1:1 | 3:4 / 小于 5MB</span
                  >
                </div>
              </el-upload>
            </div>
          </el-form-item>

          <el-form-item label="景点知识详细介绍" prop="attractions">
            <el-input
              v-model="form.attractions"
              type="textarea"
              :rows="9"
              placeholder="请输入景点的历史典故、文化内涵或解谜相关的背景知识..."
              maxlength="1000"
              show-word-limit
            />
          </el-form-item>
        </el-form>
      </div>

      <!-- 右侧：现代手机拟物化真机视口 -->
      <div class="preview-area">
        <div class="preview-header">
          <span class="badge-dot"></span>
          <span>移动端阅读呈现</span>
        </div>

        <div class="mockup-phone">
          <!-- 模拟器顶部刘海/灵动岛 -->
          <div class="phone-top-bar">
            <div class="notch"></div>
          </div>

          <!-- 模拟器屏幕视口 -->
          <div class="phone-screen">
            <!-- 手机顶部简易导航 -->
            <div class="mock-nav">
              <el-icon><ArrowLeft /></el-icon>
              <span class="nav-title">景点档案</span>
              <span class="nav-placeholder"></span>
            </div>

            <!-- 景点英雄图片区 -->
            <div class="screen-hero">
              <el-image
                v-if="form.attractionsImage"
                :src="form.attractionsImage"
                class="hero-img"
                alt="Attraction Cover"
                fit="contain"
              />
              <div v-else class="hero-placeholder">
                <el-icon><PictureFilled /></el-icon>
                <span>景点插图预览</span>
              </div>
            </div>

            <!-- 景点图文正文展示区 -->
            <div class="screen-body">
              <h3 class="screen-title">
                {{ form.attractionsTitle || "景点标题占位" }}
              </h3>
              <div class="screen-desc">
                {{
                  form.attractions ||
                  "景点历史沿革、解说辞或文化常识将在此处完整展示，支持自动断行与上下滚动查看..."
                }}
              </div>
            </div>

            <!-- 底部交互操作栏 -->
            <div class="screen-footer">
              <button class="mock-next-btn">已了解，继续探索</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import {
  Plus,
  Delete,
  PictureFilled,
  ArrowLeft,
} from "@element-plus/icons-vue";
import { ElNotification } from "element-plus";

export default defineComponent({
  name: "AttractionForm",
  components: { Plus, Delete, PictureFilled, ArrowLeft },
  props: ["dataForm"],
  data(): any {
    return {
      form: {
        attractionsTitle: "",
        attractions: "",
        attractionsImage: "",
      },
      fileList: [],
    };
  },
  computed: {
    allowUpload(): boolean {
      return Boolean(this.form.attractionsImage);
    },
    UpdateApi(): string {
      return process.env.VUE_APP_Update_API || "";
    },
  },
  watch: {
    dataForm: {
      immediate: true,
      deep: true,
      handler(newVal) {
        if (newVal) {
          this.updateForm(newVal);
        }
      },
    },
  },
  methods: {
    updateForm(dataForm: any) {
      this.form.attractions = dataForm.attractions || "";
      this.form.attractionsTitle = dataForm.attractionsTitle || "";
      this.form.attractionsImage = dataForm.attractionsImage || "";
    },
    beforeAvatarUpload(file: File): boolean {
      const isAllowedFormat =
        file.type === "image/jpeg" ||
        file.type === "image/png" ||
        file.type === "image/webp";
      const isLt5M = file.size / 1024 / 1024 < 5;

      if (!isAllowedFormat) {
        ElNotification.warning({
          message: "仅支持 JPG/PNG/WEBP 格式图片",
          offset: 100,
          duration: 3000,
        });
        return false;
      }
      if (!isLt5M) {
        ElNotification.warning({
          message: "图片体积不可超过 5MB",
          offset: 100,
          duration: 3000,
        });
        return false;
      }

      return true;
    },
    handleAvatarSuccess(response: any) {
      if (response && response.data && response.data.returnFileUrl) {
        this.form.attractionsImage = response.data.returnFileUrl;
      }
    },
    async clearImage() {
      const fileName = this.form.attractionsImage;

      if (!fileName) return;

      try {
        // 1. 匹配 action 参数签名（传对象）
        const delFinished = await this.$store.dispatch("user/delFile", {
          fileName,
        });

        // 2. 只有删除成功后才清空前端字段
        if (delFinished === 1) {
          this.form.attractionsImage = "";
        }
      } catch (error) {
        console.error("清除图片失败:", error);
      }
    },
  },
});
</script>

<style lang="less" scoped>
@primary-color: #5d5fef;
@primary-light: #f0f2ff;
@text-main: #1e293b;
@text-sub: #64748b;
@text-muted: #94a3b8;
@border-color: #e2e8f0;
@card-border: #f1f5f9;
@danger-color: #ef4444;

.form4-container {
  width: 100%;
}

// 头部说明
.section-header {
  margin-bottom: 24px;
  padding-bottom: 12px;
  border-bottom: 1px solid @border-color;
  display: flex;
  align-items: baseline;
  gap: 12px;

  .section-title {
    font-size: 17px;
    font-weight: 700;
    color: @text-main;
    margin: 0;
  }

  .section-tip {
    font-size: 13px;
    color: @text-muted;
  }
}

// 主体双栏布局
.editor-layout {
  display: flex;
  gap: 40px;
  align-items: flex-start;

  @media (max-width: 992px) {
    flex-direction: column;
    gap: 32px;
  }
}

// 左侧表单排版
.form-area {
  flex: 1;
  max-width: 600px;
  width: 100%;

  .custom-form {
    :deep(.el-form-item) {
      margin-bottom: 24px;

      .el-form-item__label {
        font-size: 13.5px;
        font-weight: 600;
        color: @text-main;
        padding-bottom: 6px;
      }
    }

    :deep(.el-input__wrapper),
    :deep(.el-textarea__inner) {
      border-radius: 8px;
      box-shadow: 0 0 0 1px @border-color inset;
      transition: all 0.2s ease;

      &:hover {
        box-shadow: 0 0 0 1px #cbd5e1 inset;
      }

      &.is-focus,
      &:focus {
        box-shadow: 0 0 0 1px @primary-color inset !important;
      }
    }
  }
}

// 封面图上传器
.cover-uploader-wrapper {
  .attraction-uploader {
    :deep(.el-upload) {
      width: 100%;
      display: block;
      cursor: pointer;
    }
  }

  .uploaded-box {
    position: relative;
    width: 100%;
    height: 180px;
    border-radius: 10px;
    overflow: hidden;
    border: 1px solid @border-color;
    background: #f8fafc;

    .cover-preview {
      width: 100%;
      height: 100%;
      display: block;
    }

    .image-error-slot {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100%;
      color: @text-muted;
      font-size: 12px;
      gap: 6px;

      .el-icon {
        font-size: 24px;
      }
    }

    .cover-mask {
      position: absolute;
      inset: 0;
      background: rgba(15, 23, 42, 0.55);
      backdrop-filter: blur(2px);
      display: flex;
      justify-content: center;
      align-items: center;
      opacity: 0;
      transition: opacity 0.25s ease;

      .action-btn {
        width: 38px;
        height: 38px;
        border-radius: 50%;
        background: #ffffff;
        color: @danger-color;
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 16px;
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
        transition: transform 0.2s;

        &:hover {
          transform: scale(1.12);
        }
      }
    }

    &:hover .cover-mask {
      opacity: 1;
    }
  }

  .upload-trigger-placeholder {
    width: 100%;
    height: 140px;
    border: 1.5px dashed #cbd5e1;
    border-radius: 10px;
    background: #f8fafc;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 6px;
    transition: all 0.25s ease;

    .uploader-icon {
      font-size: 22px;
      color: @text-sub;
    }

    .trigger-text {
      font-size: 13.5px;
      font-weight: 600;
      color: @text-main;
    }

    .trigger-sub {
      font-size: 11.5px;
      color: @text-muted;
    }

    &:hover {
      border-color: @primary-color;
      background: @primary-light;

      .uploader-icon,
      .trigger-text {
        color: @primary-color;
      }
    }
  }
}

// 右侧模拟器展示区
.preview-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;

  .preview-header {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 600;
    color: @text-sub;
    margin-bottom: 14px;

    .badge-dot {
      width: 8px;
      height: 8px;
      background: #10b981;
      border-radius: 50%;
      box-shadow: 0 0 6px rgba(16, 185, 129, 0.6);
    }
  }
}

// 模拟手机壳体（与故事剧本、提示、关卡配置完全统一）
.mockup-phone {
  width: 290px;
  height: 520px;
  background: #ffffff;
  border-radius: 36px;
  border: 10px solid #0f172a;
  box-shadow: 0 16px 36px -6px rgba(15, 23, 42, 0.16),
    0 0 0 1px rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  .phone-top-bar {
    width: 100%;
    height: 20px;
    display: flex;
    justify-content: center;
    background: #ffffff;
    z-index: 10;

    .notch {
      width: 76px;
      height: 12px;
      background: #0f172a;
      border-bottom-left-radius: 6px;
      border-bottom-right-radius: 6px;
    }
  }

  .phone-screen {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: #f8fafc;
  }

  .mock-nav {
    height: 38px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 14px;
    border-bottom: 1px solid @border-color;
    background: #ffffff;
    font-size: 13px;
    color: @text-main;

    .nav-title {
      font-weight: 700;
      font-size: 13px;
    }

    .nav-placeholder {
      width: 16px;
    }
  }

  .screen-hero {
    width: 100%;
    height: 160px;
    background: #e2e8f0;
    overflow: hidden;
    flex-shrink: 0;

    .hero-img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .hero-placeholder {
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 6px;
      color: @text-muted;
      font-size: 11px;

      .el-icon {
        font-size: 24px;
      }
    }
  }

  .screen-body {
    flex: 1;
    padding: 14px 16px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;

    .screen-title {
      font-size: 15px;
      font-weight: 700;
      color: @text-main;
      margin: 0 0 8px 0;
      line-height: 1.4;
    }

    .screen-desc {
      font-size: 12.5px;
      color: @text-sub;
      line-height: 1.65;
      white-space: pre-wrap;
      word-break: break-all;
    }
  }

  .screen-footer {
    padding: 10px 16px 14px;
    background: #ffffff;
    border-top: 1px solid @border-color;

    .mock-next-btn {
      width: 100%;
      height: 36px;
      border-radius: 18px;
      border: none;
      background: @primary-color;
      color: #ffffff;
      font-size: 13px;
      font-weight: 600;
      cursor: default;
      box-shadow: 0 3px 8px rgba(93, 95, 239, 0.3);
    }
  }
}
</style>
